import React from 'react';

const Pagination = ({ 
  currentPage, 
  totalPages, 
  onPageChange, 
  hasNextPage, 
  hasPrevPage 
}) => {
  // Generate page numbers to display
  const getPageNumbers = () => {
    const delta = 2; // Number of pages to show on each side of current page
    const range = [];
    const rangeWithDots = [];

    for (
      let i = Math.max(2, currentPage - delta);
      i <= Math.min(totalPages - 1, currentPage + delta);
      i++
    ) {
      range.push(i);
    }

    if (currentPage - delta > 2) {
      rangeWithDots.push(1, '...');
    } else {
      rangeWithDots.push(1);
    }

    rangeWithDots.push(...range);

    if (currentPage + delta < totalPages - 1) {
      rangeWithDots.push('...', totalPages);
    } else if (totalPages > 1) {
      rangeWithDots.push(totalPages);
    }

    return rangeWithDots;
  };

  if (totalPages <= 1) {
    return null;
  }

  const pageNumbers = getPageNumbers();

  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderTop: '1px solid #E5E5E5', background: 'linear-gradient(135deg, #FFFFFF 0%, #FAFAFA 100%)', padding: '1.5rem 2rem', borderRadius: '1rem', boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)' }}>
      <div style={{ display: 'flex', flex: 1, justifyContent: 'space-between' }} className="sm:hidden">
        {/* Mobile pagination */}
        <button
          onClick={() => onPageChange(currentPage - 1)}
          disabled={!hasPrevPage}
          style={{
            position: 'relative',
            display: 'inline-flex',
            alignItems: 'center',
            borderRadius: '0.75rem',
            border: '1px solid #E5E5E5',
            background: hasPrevPage ? 'linear-gradient(135deg, #FFFFFF 0%, #F5F5F5 100%)' : '#FAFAFA',
            padding: '0.65rem 1.25rem',
            fontSize: '0.875rem',
            fontWeight: '600',
            color: '#2C2C2C',
            cursor: hasPrevPage ? 'pointer' : 'not-allowed',
            opacity: hasPrevPage ? 1 : 0.5,
            boxShadow: hasPrevPage ? '0 2px 8px rgba(0, 0, 0, 0.06)' : 'none',
            transition: 'all 0.2s ease'
          }}
        >
          Previous
        </button>
        <button
          onClick={() => onPageChange(currentPage + 1)}
          disabled={!hasNextPage}
          style={{
            position: 'relative',
            marginLeft: '0.75rem',
            display: 'inline-flex',
            alignItems: 'center',
            borderRadius: '0.75rem',
            border: '1px solid #E5E5E5',
            background: hasNextPage ? 'linear-gradient(135deg, #FFFFFF 0%, #F5F5F5 100%)' : '#FAFAFA',
            padding: '0.65rem 1.25rem',
            fontSize: '0.875rem',
            fontWeight: '600',
            color: '#2C2C2C',
            cursor: hasNextPage ? 'pointer' : 'not-allowed',
            opacity: hasNextPage ? 1 : 0.5,
            boxShadow: hasNextPage ? '0 2px 8px rgba(0, 0, 0, 0.06)' : 'none',
            transition: 'all 0.2s ease'
          }}
        >
          Next
        </button>
      </div>

      <div style={{ display: 'flex', flex: 1, alignItems: 'center', justifyContent: 'space-between' }} className="hidden sm:flex">
        <div>
          <p style={{ fontSize: '0.875rem', color: '#2C2C2C' }}>
            Page <span style={{ fontWeight: '600' }}>{currentPage}</span> of{' '}
            <span style={{ fontWeight: '600' }}>{totalPages}</span>
          </p>
        </div>
        
        <div>
          <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
            {/* Previous button */}
            <button
              onClick={() => onPageChange(currentPage - 1)}
              disabled={!hasPrevPage}
              style={{
                position: 'relative',
                display: 'inline-flex',
                alignItems: 'center',
                borderTopLeftRadius: '0.5rem',
                borderBottomLeftRadius: '0.5rem',
                padding: '0.5rem',
                color: '#6B6B6B',
                border: '2px solid #D3D3D3',
                background: '#FFFFFF',
                cursor: hasPrevPage ? 'pointer' : 'not-allowed',
                opacity: hasPrevPage ? 1 : 0.5
              }}
            >
              <span style={{ position: 'absolute', width: '1px', height: '1px', padding: 0, margin: '-1px', overflow: 'hidden', clip: 'rect(0, 0, 0, 0)', whiteSpace: 'nowrap', borderWidth: 0 }}>Previous</span>
              <svg style={{ height: '1.25rem', width: '1.25rem' }} viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                <path fillRule="evenodd" d="M12.79 5.23a.75.75 0 01-.02 1.06L8.832 10l3.938 3.71a.75.75 0 11-1.04 1.08l-4.5-4.25a.75.75 0 010-1.08l4.5-4.25a.75.75 0 011.06.02z" clipRule="evenodd" />
              </svg>
            </button>

            {/* Page numbers */}
            {pageNumbers.map((page, index) => {
              if (page === '...') {
                return (
                  <span
                    key={`dots-${index}`}
                    className="relative inline-flex items-center px-4 py-2 text-sm font-semibold text-gray-700 ring-1 ring-inset ring-gray-300 focus:outline-offset-0"
                  >
                    ...
                  </span>
                );
              }

              return (
                <button
                  key={page}
                  onClick={() => onPageChange(page)}
                  style={{
                    position: 'relative',
                    display: 'inline-flex',
                    alignItems: 'center',
                    padding: '0.5rem 1rem',
                    fontSize: '0.875rem',
                    fontWeight: '600',
                    border: '2px solid #D3D3D3',
                    cursor: 'pointer',
                    background: page === currentPage
                      ? 'linear-gradient(135deg, #1E40AF 0%, #2563EB 100%)'
                      : '#FFFFFF',
                    color: page === currentPage ? 'white' : '#1E293B',
                    zIndex: page === currentPage ? 10 : 0
                  }}
                  onMouseEnter={(e) => {
                    if (page !== currentPage) {
                      e.target.style.background = '#F5F5F5';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (page !== currentPage) {
                      e.target.style.background = '#FFFFFF';
                    }
                  }}
                >
                  {page}
                </button>
              );
            })}

            {/* Next button */}
            <button
              onClick={() => onPageChange(currentPage + 1)}
              disabled={!hasNextPage}
              style={{
                position: 'relative',
                display: 'inline-flex',
                alignItems: 'center',
                borderTopRightRadius: '0.5rem',
                borderBottomRightRadius: '0.5rem',
                padding: '0.5rem',
                color: '#6B6B6B',
                border: '2px solid #D3D3D3',
                background: '#FFFFFF',
                cursor: hasNextPage ? 'pointer' : 'not-allowed',
                opacity: hasNextPage ? 1 : 0.5
              }}
            >
              <span style={{ position: 'absolute', width: '1px', height: '1px', padding: 0, margin: '-1px', overflow: 'hidden', clip: 'rect(0, 0, 0, 0)', whiteSpace: 'nowrap', borderWidth: 0 }}>Next</span>
              <svg style={{ height: '1.25rem', width: '1.25rem' }} viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                <path fillRule="evenodd" d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z" clipRule="evenodd" />
              </svg>
            </button>
          </nav>
        </div>
      </div>
    </div>
  );
};

export default Pagination;
